import React from 'react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AddButton } from '@/features/zones/components/AddButton'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '@/shared/components/molecules/Carousel'
import PlaylistCard from '@/shared/components/molecules/PlaylistCard/PlaylistCard'

interface PlaylistsSectionProps {
	directPlaylists: Playlist[]
	groupPlaylists: Playlist[]
	onAdd?: () => void
	onRemoveFromZone?: (playlist: Playlist) => void
	onRemoveFromGroup?: (playlist: Playlist) => void
	onRemoveFromBoth?: (playlist: Playlist) => void
	isLoading?: boolean
}

export const PlaylistsSection: React.FC<PlaylistsSectionProps> = ({
	directPlaylists,
	groupPlaylists,
	onAdd,
	onRemoveFromZone,
	onRemoveFromGroup,
	onRemoveFromBoth,
	isLoading = false,
}) => {
	const { t } = useTranslation('zones')

	const allPlaylists = [
		...directPlaylists.map((p) => ({ playlist: p, source: 'direct' as const })),
		...groupPlaylists
			.filter((gp) => !directPlaylists.some((dp) => dp.id === gp.id))
			.map((p) => ({ playlist: p, source: 'group' as const })),
	]

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">
						{t('details.playlists.title')}
					</h2>
				</div>
				<div className="flex gap-4">
					{[1, 2, 3].map((i) => (
						<div key={i} className="w-[200px] space-y-3">
							<div className="aspect-square rounded-lg bg-muted animate-pulse" />
							<div className="h-4 w-32 bg-muted rounded animate-pulse" />
						</div>
					))}
				</div>
			</div>
		)
	}

	if (allPlaylists.length === 0) {
		return (
			<div className="space-y-4 mb-12">
				<div className="flex items-center gap-3">
					<h2 className="text-xl font-semibold">
						{t('details.playlists.title')}
					</h2>
					{onAdd && <AddButton onAdd={onAdd} />}
				</div>
				<div className="text-sm text-muted-foreground">
					{t('details.playlists.noPlaylists')}
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-4 mb-12">
			<div className="flex items-center gap-3">
				<h2 className="text-xl font-semibold">
					{t('details.playlists.title')}
				</h2>
				{onAdd && <AddButton onAdd={onAdd} />}
			</div>

			<Carousel
				opts={{
					align: 'start',
					slidesToScroll: 1,
				}}
				className="w-full"
			>
				<CarouselContent className="-ml-4 flex flex-wrap">
					{allPlaylists.map(({ playlist, source }) => (
						<CarouselItem
							key={`${source}-${playlist.id}`}
							className="pl-4 basis-auto pb-4"
						>
							<PlaylistCard
								playlist={playlist}
								showFavoriteIcon={false}
								onDelete={
									source === 'direct'
										? groupPlaylists.some((gp) => gp.id === playlist.id) &&
											onRemoveFromBoth
											? () => onRemoveFromBoth(playlist)
											: onRemoveFromZone
												? () => onRemoveFromZone(playlist)
												: undefined
										: source === 'group' && onRemoveFromGroup
											? () => onRemoveFromGroup(playlist)
											: undefined
								}
								className="w-[200px]"
							/>
						</CarouselItem>
					))}
				</CarouselContent>
			</Carousel>
		</div>
	)
}
